// Hook personalizado para manejo de sonidos de mensajes.
// Centraliza la lógica de reproducción de audio usando Web Audio API.
// Maneja sonidos de envío y recepción de mensajes.
// Adaptado para web desde amarsi-app (que usaba Expo Audio).

import { useCallback, useRef } from 'react';

export function useMessageSounds() {
  // Referencias para los elementos de audio
  const sendSoundRef = useRef(null);
  const receiveSoundRef = useRef(null);

  // Reproduce sonido de envío de mensaje.
  // Reinicia la posición y reproduce sonido.
  const playSendSound = useCallback(async () => {
    try {
      // Verificar si estamos en navegador y si hay soporte de audio
      if (typeof window === 'undefined' || !window.Audio) {
        console.log('playSendSound: No hay soporte de audio en este entorno');
        return;
      }

      // Crear elemento de audio dinámicamente
      if (!sendSoundRef.current) {
        sendSoundRef.current = new Audio('/sounds/sound_1.mp3');
        sendSoundRef.current.volume = 0.3; // Volumen más bajo
        sendSoundRef.current.preload = 'auto';
        
        // Manejar errores de carga
        sendSoundRef.current.addEventListener('error', (e) => {
          console.log('playSendSound: Error cargando archivo de audio:', e);
        });
      }
      
      // Reiniciar y reproducir con Promise
      sendSoundRef.current.currentTime = 0;
      const playPromise = sendSoundRef.current.play();
      
      if (playPromise !== undefined) {
        await playPromise;
        console.log('playSendSound: Sonido de envío reproducido exitosamente');
      }
    } catch (error) {
      console.log('playSendSound: Error al reproducir sonido de envío:', error);
      // No lanzar error para no interrumpir el flujo
    }
  }, []);

  // Reproduce sonido de recepción de mensaje.
  // Reinicia la posición y reproduce sonido.
  const playReceiveSound = useCallback(async () => {
    try {
      // Verificar si estamos en navegador y si hay soporte de audio
      if (typeof window === 'undefined' || !window.Audio) {
        console.log('playReceiveSound: No hay soporte de audio en este entorno');
        return;
      }

      // Crear elemento de audio dinámicamente
      if (!receiveSoundRef.current) {
        receiveSoundRef.current = new Audio('/sounds/sound_2.mp3');
        receiveSoundRef.current.volume = 0.3; // Volumen más bajo
        receiveSoundRef.current.preload = 'auto';
        
        // Manejar errores de carga
        receiveSoundRef.current.addEventListener('error', (e) => {
          console.log('playReceiveSound: Error cargando archivo de audio:', e);
        });
      }
      
      // Reiniciar y reproducir con Promise
      receiveSoundRef.current.currentTime = 0;
      const playPromise = receiveSoundRef.current.play();
      
      if (playPromise !== undefined) {
        await playPromise;
        console.log('playReceiveSound: Sonido de recepción reproducido exitosamente');
      }
    } catch (error) {
      console.log('playReceiveSound: Error al reproducir sonido de recepción:', error);
      // No lanzar error para no interrumpir el flujo
    }
  }, []);

  // Cleanup al desmontar
  const cleanup = useCallback(() => {
    if (sendSoundRef.current) {
      sendSoundRef.current.pause();
      sendSoundRef.current = null;
    }
    if (receiveSoundRef.current) {
      receiveSoundRef.current.pause();
      receiveSoundRef.current = null;
    }
  }, []);

  // Retorna las funciones de reproducción de sonido
  return {
    playSendSound,
    playReceiveSound,
    cleanup
  };
}
